(ns app.front.view.periodic-inspection.new-periodic-inspection
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.select :as ui-select]
            [app.front.view.property.dashboard.subs :as property-dashboard-subs]
            [app.i18n.model :as i18n-model]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::periodic-inspection-modal-open?
  :-> ::periodic-inspection-modal-open?)

(rf/reg-event-db ::close-periodic-inspection-modal
  #(dissoc % ::periodic-inspection-modal-open? ::data))

(rf/reg-event-db ::open-periodic-inspection-modal
  (fn [db [_ property-id]]
    (-> db
        (assoc-in [::data :property-id] property-id)
        (assoc-in [::data :open-new?] true)
        (assoc ::periodic-inspection-modal-open? true))))

(rf/reg-sub ::inspections-created-modal-open?
  :-> ::inspections-created-modal-open?)

(rf/reg-event-db ::close-inspections-created-modal
  #(dissoc % ::inspections-created-modal-open?))

(rf/reg-event-fx ::open-periodic-inspections-created-modal
  (fn [{db :db} [_ status response]]
    {:db         (assoc db ::inspections-created-modal-open? true)
     :dispatch-n [[::close-periodic-inspection-modal]
                  [::create-workspace.success false status response]]}))

(rf/reg-sub ::data
  :-> ::data)

(rf/reg-sub ::field-value
  :<- [::data]
  (fn [data [_ key]]
    (key data)))

(rf/reg-sub ::invalid-data?
  :<- [::field-value :authority-group]
  :<- [::field-value :category-config]
  :<- [::field-value :work-type-config]
  :<- [::field-value :work-target-config]
  :<- [::field-value :target-month]
  :<- [::work-type-required?]
  :<- [::work-target-required?]
  (fn [[authority-group category work-type work-target target-month work-type-required? work-target-required?]]
    (or (nil? authority-group)
        (nil? category)
        (and (nil? work-type) work-type-required?)
        (and (nil? work-target) work-target-required?)
        (nil? target-month))))

(rf/reg-sub ::permit-type-config
  :<- [::field-value :authority-group]
  :<- [::entities/context]
  (fn [[group ctx] _]
    (->> (entities/find-entities ctx {:group.config/level  :group-config.level/permit-type
                                      :group.config/group  (:db/id group)
                                      :domain/state        :group-config.state/enabled
                                      :group.config/target :permit-type/PI})
         (filter du/is-active?)
         first)))

(rf/reg-event-fx
  ::set-authority-group
  (fn [{:keys [db]} [_ group]]
    {:comm/command {:command    :periodic-inspection/get-group-config
                    :method     :get
                    :data       {:authority-group-id (:db/id group)}
                    :on-success [::permit-type-configs-fetched]}
     :db           (assoc-in db [::data :authority-group] group)}))

(rf/reg-event-db ::set-field-value
  (fn [db [_ key value]]
    (assoc-in db [::data key] value)))

(rf/reg-event-db ::unset-field-values
  (fn [db [_ keys]]
    (update db ::data #(apply dissoc % keys))))

(defn get-child-configs [lang config]
  (->> config
       config-model/child-configs
       (filter du/is-active?)
       (config-model/sort-group-configs lang)))

(rf/reg-sub ::category-group-configs
  :<- [::permit-type-config]
  :<- [:language]
  (fn [[root-config language] _]
    (get-child-configs language root-config)))

(rf/reg-sub ::work-type-group-configs
  :<- [::field-value :category-config]
  :<- [:language]
  (fn [[selected-category language] _]
    (->> selected-category
         (get-child-configs language))))

(rf/reg-sub ::work-type-required?
  :<- [::work-type-group-configs]
  :-> #(-> % empty? not))

(rf/reg-sub ::work-target-group-configs
  :<- [::field-value :work-type-config]
  :<- [:language]
  (fn [[selected-work-type language] _]
    (->> selected-work-type
         (get-child-configs language))))

(rf/reg-sub ::work-target-required?
  :<- [::work-target-group-configs]
  :-> #(-> % empty? not))

(rf/reg-event-fx ::permit-type-configs-fetched
  (fn [_ [_ status entities]]
    {:dispatch-n [[::entities/merge-entity-db status entities]]}))

(rf/reg-event-fx ::create-workspace.success
  (fn [_ [_ open-new? status response]]
    (let [workspace-id (entities/property-of-some-in-apply-changes
                         :workspace/id
                         response
                         (partial remove :workspace/parent-workspace))] ;; Make sure we get the parent ws id
      {:fx [[:dispatch [::entities/apply-changes status response]]
            [:dispatch [::close-periodic-inspection-modal]]
            (when open-new? [:route/push {:route :view/workspace :params {:workspace-id workspace-id}}])]})))

(rf/reg-event-fx ::create-periodic-inspection-workspace
  (fn [{:keys [db]} [_ multi-select]]
    (let [data (::data db)]
      {:dispatch [:comm/command-monitored {:command    :periodic-inspection/create-workspaces
                                           :method     :post
                                           :data       (p/assoc-if
                                                         (merge {:authority-group-id (-> data :authority-group :db/id)
                                                                 :category-id        (-> data :category-config :db/id)
                                                                 :target-month       (:target-month data)}
                                                                (if multi-select
                                                                  {:property-ids (map parse-long multi-select)}
                                                                  {:property-ids [(:property-id data)]}))
                                                         :work-type-id (-> data :work-type-config :db/id)
                                                         :work-target-id (-> data :work-target-config :db/id))
                                           :notify     {:success :workspace/created}
                                           :on-success (if multi-select
                                                         [::open-periodic-inspections-created-modal]
                                                         [::create-workspace.success (:open-new? data)])
                                           :on-error   [::create-workspace.error]}]})))

(defn periodic-inspection-modal [tr]
  (let [open?                 (<sub [::periodic-inspection-modal-open?])
        close-modal-fn        #(>evt [::close-periodic-inspection-modal])
        lang                  (<sub [:language])
        multi-select          (and (nil? (<sub [::field-value :property-id]))
                                   (seq (<sub [:app.front.view.property.dashboard.widgets.search/selected-properties])))
        work-types            (<sub [::work-type-group-configs])
        work-type-required?   (<sub [::work-type-required?])
        work-targets          (<sub [::work-target-group-configs])
        work-target-required? (<sub [::work-target-required?])]
    (if (<sub [:comm/command-processing? :periodic-inspection/create-workspace])
      (cc/loading-spinner)
      [ui-modal/composed-input-modal
       {:id       "periodic-inspection-modal"
        :open     open?
        :on-close close-modal-fn
        :size     :md}
       (cc/modal-header {:close-modal close-modal-fn
                         :title       (cond-> (tr [:property/create-periodic-inspection])
                                        multi-select (str " (" (count multi-select) ")"))})
       (when open?
         (cc/modal-body
           {}
           [:div.flex.flex-col.space-y-4
            [ui-select/select {:id           "authority-group-select"
                               :class-name   "full-width-select"
                               :allow-empty? false
                               :required     true
                               :empty-text   (tr [:select/select])
                               :label-text   (tr [:periodic-inspection/authority-group])
                               :values       (<sub [::property-dashboard-subs/authority-groups-with-periodic-inspections])
                               :val->str     :group/name
                               :value        (<sub [::field-value :authority-group])
                               :on-change    (fn [val]
                                               (>evt [::unset-field-values [:category-config
                                                                            :work-type-config
                                                                            :work-target-config]])
                                               (>evt [::set-authority-group val]))}]
            [ui-select/select {:id           "category-select"
                               :class-name   "full-width-select"
                               :allow-empty? false
                               :required     true
                               :empty-text   (tr [:select/select])
                               :label-text   (tr [:periodic-inspection/category])
                               :values       (<sub [::category-group-configs])
                               :val->str     #(i18n-model/get-text (:group.config/names %) lang)
                               :value        (<sub [::field-value :category-config])
                               :on-change    (fn [val]
                                               (>evt [::unset-field-values [:work-type-config
                                                                            :work-target-config]])
                                               (>evt [::set-field-value :category-config val]))}]
            [ui-select/select {:id           "work-type-select"
                               :class-name   "full-width-select"
                               :allow-empty? (not work-type-required?)
                               :required     work-type-required?
                               :empty-text   (tr [:select/select])
                               :label-text   (tr [:periodic-inspection/work-type])
                               :values       work-types
                               :val->str     #(i18n-model/get-text (:group.config/names %) lang)
                               :value        (<sub [::field-value :work-type-config])
                               :on-change    (fn [val]
                                               (>evt [::unset-field-values [:work-target-config]])
                                               (>evt [::set-field-value :work-type-config val]))}]
            [ui-select/select {:id           "work-target-select"
                               :class-name   "full-width-select"
                               :allow-empty? (not work-target-required?)
                               :required     work-target-required?
                               :empty-text   (tr [:select/select])
                               :label-text   (tr [:periodic-inspection/work-target])
                               :values       work-targets
                               :val->str     #(i18n-model/get-text (:group.config/names %) lang)
                               :value        (<sub [::field-value :work-target-config])
                               :on-change    #(>evt [::set-field-value :work-target-config %])}]
            [ui-select/select {:id           "inspection-month-select"
                               :label-text   (tr [:periodic-parent/target-month])
                               :values       (range 1 13)
                               :val->str     #(tr [(keyword "date.month" (str (dec %)))])
                               :allow-empty? false
                               :required     true
                               :value        (<sub [::field-value :target-month])
                               :on-change    #(>evt [::set-field-value :target-month %])}]
            (when-not multi-select
              (cc/checkbox {:id         "open-created-workspace"
                            :label-text (tr [:periodic-inspection/open-new-workspace])
                            :checked    (<sub [::field-value :open-new?])
                            :on-change  #(>evt [::set-field-value :open-new? %])}))]))
       (cc/modal-footer
         {}
         (cc/button {:kind     :secondary
                     :on-click close-modal-fn}
                    (tr [:cancel]))
         (cc/button {:kind     :primary
                     :disabled (<sub [::invalid-data?])
                     :on-click #(>evt [::create-periodic-inspection-workspace multi-select])}
                    (tr [:create])))])))

(defn inspections-created-modal [tr]
  (let [open?          (<sub [::inspections-created-modal-open?])
        close-modal-fn #(>evt [::close-inspections-created-modal])
        multi-select   (<sub [:app.front.view.property.dashboard.widgets.search/selected-properties])]
    (if (<sub [:comm/command-processing? :periodic-inspection/create-workspace])
      (cc/loading-spinner)
      [ui-modal/composed-input-modal
       {:id       "inspections-created-dialog"
        :open     open?
        :on-close close-modal-fn
        :size     :md}
       (cc/modal-header {:close-modal close-modal-fn
                         :title       (if (= 1 (count multi-select))
                                        (tr [:inspection/nr-created])
                                        (tr [:inspection/nr-created-plural]
                                            [(count multi-select)]))})

       (cc/modal-body
         {}
         [:div.flex.flex-col.space-y-4])

       (cc/modal-footer
         {}
         (cc/button {:kind     :secondary
                     :on-click close-modal-fn}
                    (tr [:ok])))])))
